import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	signal,
	TemplateRef,
	ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { HotToastService } from '@ngxpert/hot-toast';
import { firstValueFrom, map, Observable } from 'rxjs';

import { PlanId } from '@reach/interfaces/plans/plan.interface';
import { Theme } from '@reach/interfaces/settings/theme.enum';
import { UserType } from '@reach/interfaces/user/user-type.enum';

import { SessionService, SettingsService } from '~app-client/core/services';
import { detectBrowser, ReachBrowser } from '~app-client/home/_internal/components';
import { SidenavService } from '~app-client-item/home/services/sidenav.service';
import { ProgressLineModule } from '~app-ui/progress/line';
import { ReachUserThumbnailModule } from '~app-ui/thumbnails/users';
import { UiNewButtons } from '~app-ui-new/buttons';
import { ReachMenuComponent, ReachMenuModule } from '~app-ui-new/menu';
import { MenuDesktopType } from '~ng-common-lib/ui/menu/types/desktop';
import { TooltipsModule } from '~ng-common-lib/ui/tooltips';

import { WidthSize } from '../../../../../ui-new/menu/components/floating/floating.component';

const ALL_THEMES = Object.values(Theme);

@Component({
	selector: 'sidenav-footer',
	templateUrl: 'sidenav-footer.component.html',
	styleUrls: ['sidenav-footer.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		CommonModule,
		TranslocoModule,
		ReachUserThumbnailModule,
		UiNewButtons,
		RouterModule,
		TooltipsModule,
		ProgressLineModule,
		ReachMenuModule,
		ReachMenuComponent,
	],
	providers: [{ provide: TRANSLOCO_SCOPE, useValue: { scope: 'general' } }],
	host: {
		class: 'block',
	},
})
export class SidenavFooterComponent {
	public readonly isNativePlatform = Capacitor.isNativePlatform();
	private readonly browser = detectBrowser();

	public readonly MenuDesktopType = MenuDesktopType;
	public readonly WidthSize = WidthSize;

	public readonly showBrowserExtension =
		!this.isNativePlatform &&
		[ReachBrowser.BRAVE, ReachBrowser.EDGE, ReachBrowser.CHROME, ReachBrowser.FIREFOX].includes(
			this.browser
		);

	public readonly themeMap: Record<Theme, { icon: string; tooltipKey: string }> = {
		[Theme.LIGHT]: { icon: 'icon-sun-light', tooltipKey: 'general.light_mode' },
		[Theme.DARK]: { icon: 'icon-moon-dark', tooltipKey: 'general.dark_mode' },
		[Theme.AUTO]: { icon: 'icon-device-monitor', tooltipKey: 'general.auto_mode' },
	};

	@ViewChild('errorTpl')
	private errorTpl: TemplateRef<void>;

	public readonly sidenavService = inject(SidenavService);
	private readonly sessionService = inject(SessionService);
	private readonly settingsService = inject(SettingsService);
	private readonly hotToast = inject(HotToastService);
	private readonly router = inject(Router);

	public readonly UserType = UserType;
	public readonly UserStatus = PlanId;
	public readonly Theme = Theme;
	public readonly $hovering = signal(false);
	public readonly user$ = this.sessionService.user$;
	public readonly $user = toSignal(this.user$);
	public readonly $menuStatus = signal<boolean>(false);

	public readonly allThemes = ALL_THEMES;

	public readonly trialStartedAt$ = this.user$.pipe(
		map((user) => {
			if (!user) return null;

			return user.currentPlanSubscription?.planId === PlanId.TRIAL &&
				user.currentPlanSubscription?.activatedAt
				? new Date(user.currentPlanSubscription.activatedAt)
				: null;
		})
	);
	public readonly $trialStartedAt = toSignal(this.trialStartedAt$);

	public readonly trialExpiresAt$ = this.user$.pipe(
		map((user) => {
			if (!user) return null;

			return user.currentPlanSubscription?.planId === PlanId.TRIAL &&
				user.currentPlanSubscription?.expiresAt
				? new Date(user.currentPlanSubscription.expiresAt)
				: null;
		})
	);
	public readonly $trialExpiresAt = toSignal(this.trialExpiresAt$);

	public readonly theme$ = this.settingsService.settings$.pipe(
		map((settings) => settings.general.theme)
	);
	public readonly $theme = toSignal(this.theme$);

	public readonly nextTheme$: Observable<{
		icon: string;
		tooltipKey: string;
		theme: Theme;
	}> = this.theme$.pipe(
		map((theme) => {
			const currentIndex = ALL_THEMES.indexOf(theme);
			const nextIndex = (currentIndex + 1) % ALL_THEMES.length;
			return {
				...this.themeMap[ALL_THEMES[nextIndex]],
				theme: ALL_THEMES[nextIndex],
			};
		})
	);

	public readonly currentTheme$: Observable<{
		icon: string;
		tooltipKey: string;
		theme: Theme;
	}> = this.theme$.pipe(
		map((theme) => {
			const currentIndex = ALL_THEMES.indexOf(theme);
			return {
				...this.themeMap[ALL_THEMES[currentIndex]],
				theme: ALL_THEMES[currentIndex],
			};
		})
	);

	public readonly $daysSinceTrialStarted = computed(() => {
		const trialStartedAt = this.$trialStartedAt();
		if (!trialStartedAt) return 0;
		return this.getDaysSince(trialStartedAt) + 1;
	});

	public readonly $trialTotalDays = computed(() => {
		const trialStartedAt = this.$trialStartedAt();
		const trialExpiresAt = this.$trialExpiresAt();
		if (!trialStartedAt || !trialExpiresAt) return 0;
		return this.getDaysBetween(trialStartedAt, trialExpiresAt);
	});

	public readonly $daysUntilTrialExpires = computed(() => {
		const trialExpiresAt = this.$trialExpiresAt();
		if (!trialExpiresAt) return 0;

		const days = this.getDaysBetween(new Date(), trialExpiresAt) - 1;
		return days < 0 ? 0 : days;
	});

	public async changeTheme(): Promise<void> {
		try {
			const nextTheme = await firstValueFrom(this.nextTheme$);
			await this.settingsService.changeValue({
				general: { theme: nextTheme.theme.toLocaleLowerCase() as Theme },
			});
		} catch {
			this.hotToast.error(this.errorTpl, {
				id: 'CHANGE_THEME_ERROR',
			});
		}
	}

	public async setTheme(theme: Theme): Promise<void> {
		if (this.$theme() === theme) return;

		try {
			await this.settingsService.changeValue({
				general: { theme: theme.toLocaleLowerCase() as Theme },
			});
		} catch {
			this.hotToast.error(this.errorTpl, {
				id: 'CHANGE_THEME_ERROR',
			});
		}
	}

	public getThemePillTransform(): string {
		const currentTheme = this.$theme();
		const themeIndex = ALL_THEMES.indexOf(currentTheme ?? Theme.AUTO);
		return `translateX(${Math.max(0, themeIndex) * 100}%)`;
	}

	private getDaysBetween(start: Date, end: Date): number {
		const diffMs = end.getTime() - start.getTime();
		const MS_PER_DAY = 1000 * 60 * 60 * 24;
		return Math.max(0, Math.ceil(diffMs / MS_PER_DAY));
	}

	private getDaysSince(date: Date): number {
		const diffMs = Date.now() - date.getTime();
		return Math.floor(diffMs / (1000 * 60 * 60 * 24));
	}

	public toggleMenuSettings(): void {
		this.$menuStatus.set(!this.$menuStatus());
	}

	public async navigateToProfileSettings(): Promise<void> {
		await this.router.navigate(['/', 'home', 'profile']);
	}

	public async signOut(): Promise<void> {
		await this.sessionService.logout();
		await this.router.navigate(['/']);
	}

	public openExtensionPage(): void {
		const isFirefox = this.browser === ReachBrowser.FIREFOX;
		const firefoxLink = 'https://addons.mozilla.org/en-US/firefox/addon/reach-web-saver/';
		const chromiumLink =
			'https://chrome.google.com/webstore/detail/reach-web-clipper/kegfimibbdadlkgbiijhljlolgfndcem';
		const url = isFirefox ? firefoxLink : chromiumLink;
		Browser.open({ url });
	}
}
